import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { getStyles, getScrollBarWidth, addToArray } from '../../../shared/utility';

import chroma from 'chroma-js';

import Button from '../Button/Button';

import localStyles from './HorizontalScroll.scss';
const styles = getStyles(localStyles);

class HorizontalScroll extends PureComponent {

    /* * * * * * * * * *
     * REACHT METHODS  *
     * * * * * * * * * */
    constructor(props) {
        super(props);
        this.state = {
            dimensions: { container: 0, items: 0 },
            canScroll: false,
            canScrollPrev: false,
            canScrollNext: false,
            activeChildIndex: 0,
            children: null
        };
        this.scrollPosition = 0;
        this.dragStartPosition = 0;
        this.scrollBarWidth = getScrollBarWidth();

        this.refItems = React.createRef();
        this.refContainer = React.createRef();
    }

    componentDidMount() {
        this.initialize();
        window.addEventListener('resize', this.initialize);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.children !== this.props.children) {
            this.initialize();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.initialize);
    }

    /* * * * * * * * * *
     * CUSTOM METHODS  *
     * * * * * * * * * */
    initialize = () => {
        const children = [...(this.props.selectorQuery ?
            this.refItems.current.querySelectorAll(this.props.selectorQuery) :
            this.refItems.current.children)];

        // first fix SubPixel on items
        const parent = this.refContainer.current.parentNode;
        parent.removeAttribute('style');
        const width = parent.getBoundingClientRect().width;
        parent.style.width = `${Math.floor(width)}px`;

        this.refItems.current.removeAttribute('style');
        const height = this.refItems.current.getBoundingClientRect().height;
        this.refItems.current.style.height = `${Math.floor(height+1)}px`;
        if(this.scrollBarWidth){
            this.refItems.current.style.marginBottom = `-${this.scrollBarWidth}px`;
        }

        const dimensions = {
            container: this.refContainer.current.clientWidth,
            items: this.refItems.current.scrollWidth,
        };
        const distance = Math.max(0, dimensions.items - dimensions.container);

        const canScroll = dimensions.items > dimensions.container;

        this.toggleDraggableElements(!canScroll);

        this.setState({
            dimensions: { ...dimensions, distance: distance },
            canScroll: canScroll,
            canScrollPrev: this.refItems.current.scrollLeft > 0,
            canScrollNext: this.refItems.current.scrollLeft < distance,
            children: children,
            activeChildIndex: this.getActiveChildIndex(0, false, children)
        });
    }

    getActiveChildIndex = (delta, scrollLeft, children) => {
        scrollLeft = !isNaN(scrollLeft) ? scrollLeft : this.refItems.current.scrollLeft;
        return (children || this.state.children)
            .reduce((active, element, index) => {
                const diff = Math.abs(scrollLeft - element.offsetLeft);
                if (diff < active.diff || active.diff === -1) {
                    return { index: index, diff: diff };
                }
                return active;
            }, { index: 0, diff: -1 }).index + (delta || 0);
    }

    scrollTo = (index) => {
        index = Math.max(0, Math.min(this.state.children.length - 1, index));
        this.setScrollLeft(this.state.children[index].offsetLeft);
    }

    setScrollLeft = (scrollLeft) => {
        if (scrollLeft !== this.refItems.current.scrollLeft) {
            this.refItems.current.scrollLeft = scrollLeft;
        }
    }

    handleScroll = () => {
        const scrollLeft = this.refItems.current.scrollLeft;

        this.setState({
            index: this.getActiveChildIndex(0, scrollLeft),
            canScrollPrev: scrollLeft > 0,
            canScrollNext: scrollLeft < this.state.dimensions.distance
        });
    }

    /* * * * * * * * *
     * DRAG METHODS  *
     * * * * * * * * */
    toggleDraggableElements = (draggable) => [...this.refItems.current.querySelectorAll('img, a[href]')]
        .forEach((item) =>
            draggable ?
                item.removeAttribute('draggable') :
                item.setAttribute('draggable', 'false')
        );

    dragStart = (event) => {
        if (event.button !== 0 || !this.state.canScroll) {
            return;
        }

        this.scrollPosition = this.refItems.current.scrollLeft;
        this.dragStartPosition = event.clientX;

        this.refContainer.current.addEventListener('mousemove', this.drag);
        this.refContainer.current.addEventListener('mouseup', this.dragEnd);
        this.refContainer.current.addEventListener('mouseleave', this.dragEnd);
    }

    drag = (event) => {
        this.refContainer.current.classList.add(styles('horizontal-scroll__container--dragging'));
        this.refItems.current.scrollLeft = this.scrollPosition + this.dragStartPosition - event.clientX;
        this.setScrollLeft(this.scrollPosition + this.dragStartPosition - event.clientX);
    }

    dragEnd = () => {
        this.refContainer.current.classList.remove(styles('horizontal-scroll__container--dragging'));
        this.refContainer.current.removeEventListener('mousemove', this.drag);
        this.refContainer.current.removeEventListener('mouseup', this.dragEnd);
        this.refContainer.current.removeEventListener('mouseleave', this.dragEnd);
    }

    /* * * * * * * * * *
     * EVENT HANDLERS  *
     * * * * * * * * * */
    buttonOnMouseDown = (event) => event.stopPropagation();
    prevButtonOnClick = () => this.scrollTo(this.getActiveChildIndex(-1));
    nextButtonOnClick = () => this.scrollTo(this.getActiveChildIndex(1));

    /* * * * * *
     * RENDER  *
     * * * * * */
    render() {

        /* * * * * * *
         * OVERLAYS  *
         * * * * * * */
        let prevOverlay = null,
            nextOverlay = null;

        if (!this.props.noOverlay) {
            const prevOverlayStyle = {};
            const prevOverlayClasses = ['horizontal-scroll__overlay', 'horizontal-scroll__overlay--prev'];
            if (!this.state.canScrollPrev) {
                prevOverlayClasses.push('horizontal-scroll__overlay--hidden', 'horizontal-scroll__overlay--prev--hidden');
            }

            const nextOverlayStyle = {};
            const nextOverlayClasses = ['horizontal-scroll__overlay', 'horizontal-scroll__overlay--next'];
            if (!this.state.canScrollNext) {
                nextOverlayClasses.push('horizontal-scroll__overlay--hidden', 'horizontal-scroll__overlay--next--hidden');
            }

            if (this.props.backgroundColor) {
                const color = chroma(this.props.backgroundColor);
                prevOverlayStyle.backgroundImage = `linear-gradient(to right, ${this.props.backgroundColor} 50%, ${color.alpha(0).css()})`;
                nextOverlayStyle.backgroundImage = `linear-gradient(to left, ${this.props.backgroundColor} 50%, ${color.alpha(0).css()})`;
            }

            prevOverlay = <div className={styles(prevOverlayClasses)} style={prevOverlayStyle}/>;
            nextOverlay = <div className={styles(nextOverlayClasses)} style={nextOverlayStyle}/>;
        }

        /* * * * * *
         * BUTTONS *
         * * * * * */
        let prevButton = null,
            nextButton = null;

        if (!this.props.hideButtons) {
            const prevButtonClasses = [
                'horizontal-scroll__button',
                'horizontal-scroll__button--prev',
                this.state.canScrollPrev ? null : 'horizontal-scroll__button--hidden',
                this.props.stretched ? 'horizontal-scroll__button--prev--stretched' : null
            ];

            prevButton = (
                <Button
                    modifiers={['circle', (this.props.buttonSize || 'm'), 'white']}
                    classes={styles(prevButtonClasses)}
                    events={{
                        mouseDown: this.buttonOnMouseDown,
                        click: this.prevButtonOnClick
                    }}
                    icon={'arrow-left'}
                    iconColor={'green'}
                />
            );

            const nextButtonClasses = [
                'horizontal-scroll__button',
                'horizontal-scroll__button--next',
                this.state.canScrollNext ? null : 'horizontal-scroll__button--hidden',
                this.props.stretched ? 'horizontal-scroll__button--next--stretched' : null
            ];

            nextButton = (
                <Button
                    modifiers={['circle', (this.props.buttonSize || 'm'), 'white']}
                    classes={styles(nextButtonClasses)}
                    events={{
                        mouseDown: this.buttonOnMouseDown,
                        click: this.nextButtonOnClick
                    }}
                    icon={'arrow-right'}
                    iconColor={'green'}
                />
            );
        }

        /* * * * * * *
         * CONTAINER *
         * * * * * * */
        let containerClasses = ['horizontal-scroll__container', this.state.canScroll ? 'horizontal-scroll__container--can-scroll' : null];
        containerClasses = addToArray(this.props.classes, containerClasses);

        return (
            <div className={styles('horizontal-scroll', this.props.stretched ? 'horizontal-scroll--stretched' : null)}>
                {prevButton}
                <div
                    className={styles(containerClasses)}
                    ref={this.refContainer}
                    onMouseDown={this.dragStart}
                    onScroll={this.handleScroll}
                >
                    <div className={styles('horizontal-scroll__items')} ref={this.refItems}>
                        {this.props.children}
                    </div>
                    {prevOverlay}
                    {nextOverlay}
                </div>
                {nextButton}
            </div>
        );
    }
}

HorizontalScroll.propTypes = {
    // properties
    children: PropTypes.node.isRequired,
    selectorQuery: PropTypes.string.isRequired,
    classes: PropTypes.string,
    backgroundColor: PropTypes.string,
    buttonSize: PropTypes.string,
    hideButtons: PropTypes.bool,
    noOverlay: PropTypes.bool,
    stretched: PropTypes.bool
};

export default HorizontalScroll;



// WEBPACK FOOTER //
// ./src/components/UI/HorizontalScroll/HorizontalScroll.js